import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import ScrollShot from '../../components/ui/ScrollShot';
import useGsap from '../../motion/useGsap';
import { HERO_PROJECTS } from '../../data/projects';
import { prefersReducedMotion, isDesktop } from '../../motion/tokens';

function pad(n) {
  return String(n).padStart(2, '0');
}

// Six flagship projects in a strip that slides sideways while the page keeps
// scrolling — no pinning, so the scroll never pauses. On touch the strip is a
// plain snap-scrolling row.
export default function WorkReel() {
  const ref = useRef(null);
  const trackRef = useRef(null);
  const items = HERO_PROJECTS.slice(0, 6);

  useGsap(ref, (gsap) => {
    if (prefersReducedMotion() || !isDesktop()) return;
    const track = trackRef.current;
    const distance = () => Math.max(0, track.scrollWidth - window.innerWidth + 96);
    gsap.fromTo(
      track,
      { x: 0 },
      {
        x: () => -distance(),
        ease: 'none',
        scrollTrigger: { trigger: ref.current, start: 'top bottom', end: 'bottom top', scrub: true, invalidateOnRefresh: true },
      }
    );
  }, []);

  const trackCls = 'mt-8 flex gap-5 overflow-x-auto px-5 pb-4 snap-x snap-mandatory sm:px-8 lg:overflow-visible lg:px-12 lg:pb-0 lg:will-change-transform';

  return (
    <section ref={ref} className="relative overflow-hidden border-t border-line bg-deep py-16 md:py-24" id="work-reel">
      <div className="container-x flex items-end justify-between">
        <h2 className="reveal display-lg max-w-[14ch]">Selected work</h2>
        <Link to="/work" className="reveal inline-flex items-center gap-2 text-sky transition-colors hover:text-cream">
          See all work <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div ref={trackRef} className={trackCls} style={{ scrollbarWidth: 'none' }}>
        {items.map((p, i) => (
          <Link key={p.slug} to={'/work/' + p.slug} className="group relative w-[82vw] shrink-0 snap-start sm:w-[60vw] lg:w-[38vw] xl:w-[32vw]">
            <div className="overflow-hidden rounded-2xl border border-line">
              <ScrollShot mediaKey={p.media[0].key} src={p.media[0].src} alt={p.title} className="aspect-[4/3]" priority={i === 0} />
            </div>
            <div className="mt-4 flex items-baseline justify-between gap-4">
              <div>
                <h3 className="font-display text-2xl font-medium tracking-tight">{p.title}</h3>
                <p className="mt-1 text-sm text-mist">{p.category}</p>
              </div>
              <span className="shrink-0 whitespace-nowrap text-sm tabular-nums text-dim">{pad(i + 1)} / {pad(items.length)}</span>
            </div>
          </Link>
        ))}
        <Link to="/work" className="flex w-[60vw] shrink-0 snap-start items-center justify-center rounded-2xl border border-dashed border-line text-mist transition-colors hover:border-cream/40 hover:text-cream sm:w-[40vw] lg:w-[20vw]">
          All projects <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}
